/**
 * The stokvel portal's endpoints.
 *
 * Typed against `/api/v1/stokvels/`. Most writes answer with the whole group as
 * it now is, so a screen replaces its copy rather than refetching — one round
 * trip on a prepaid bundle instead of two.
 */

import { api } from "@/api/client";

export type StokvelKind =
  "SAVINGS" | "GROCERY" | "BURIAL" | "INVESTMENT" | "OTHER";
export type Frequency =
  "WEEKLY" | "FORTNIGHTLY" | "MONTHLY" | "QUARTERLY" | "ANNUALLY";
export type StokvelStatus = "PENDING_SETUP" | "ACTIVE" | "SUSPENDED";
export type MemberRole = "CHAIRPERSON" | "TREASURER" | "MEMBER";
export type MembershipStatus =
  "REQUESTED" | "ACTIVE" | "DECLINED" | "SUSPENDED" | "REMOVED" | "LEFT";
export type ContributionStatus = "PAID" | "PAID_LATE" | "OUTSTANDING";
export type MemberAction = "approve" | "decline" | "remove" | "treasurer";

export const KIND_LABELS: Record<StokvelKind, string> = {
  SAVINGS: "Savings",
  GROCERY: "Grocery",
  BURIAL: "Burial",
  INVESTMENT: "Investment",
  OTHER: "Other",
};

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  WEEKLY: "Weekly",
  FORTNIGHTLY: "Every two weeks",
  MONTHLY: "Monthly",
  QUARTERLY: "Every three months",
  ANNUALLY: "Once a year",
};

export const ROLE_LABELS: Record<MemberRole, string> = {
  CHAIRPERSON: "Chairperson",
  TREASURER: "Treasurer",
  MEMBER: "Member",
};

export interface MyStokvel {
  id: string;
  name: string;
  kind: StokvelKind;
  stokvelStatus: StokvelStatus;
  area: string;
  memberCount: number;
  role: MemberRole;
  membershipStatus: MembershipStatus;
  /** The newest contribution round, for the ring on the home screen. */
  latestPeriod: {
    label: string;
    paid: number;
    members: number;
    myStatus: ContributionStatus;
  } | null;
}

export interface SearchResult {
  id: string;
  name: string;
  kind: StokvelKind;
  status: StokvelStatus;
  area: string;
  memberCount: number;
}

export interface StokvelProfile {
  id: string;
  name: string;
  kind: StokvelKind;
  status: StokvelStatus;
  meetingFrequency: Frequency;
  contributionFrequency: Frequency;
  expectedMembers: number;
  contributionAmount: string | null;
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
  area: string;
  hasConstitution: boolean;
  constitutionUploadedAt: string | null;
  activatedAt: string | null;
  /** The chairperson's alone to share. Null for everybody else. */
  inviteCode: string | null;
  /** The public page on the Stokkie Stokvel website. */
  slug: string | null;
  isPublic: boolean;
  acceptingMembers: boolean;
  about: string;
  /** Why the page is held off the website. Empty means it shows. */
  publicReviewReasons: string[];
  plan: GroupPlan;
}

export type PlanCode = "BRONZE" | "SILVER" | "GOLD" | "PLATINUM";
export type PlanFeature = "BULK_BUYING" | "GROUP_SELLING" | "ASSETS";

export interface Plan {
  code: PlanCode;
  label: string;
  /** Rand per month, as a string: "199.00". */
  price: string;
  /** Null is no limit. */
  memberLimit: number | null;
  features: PlanFeature[];
  highlights: string[];
}

export interface GroupPlan extends Plan {
  activeMembers: number;
}

export const toPlan = (p: Record<string, unknown>): Plan => ({
  code: p.code as PlanCode,
  label: p.label as string,
  price: p.price as string,
  memberLimit: (p.member_limit as number | null) ?? null,
  features: (p.features as PlanFeature[]) ?? [],
  highlights: (p.highlights as string[]) ?? [],
});

export interface Member {
  id: string;
  userId: string;
  name: string;
  /** Only where the member chose to share it, or it is the caller's own. */
  phone: string | null;
  role: MemberRole;
  status: MembershipStatus;
  joinedAt: string | null;
  requestedAt: string | null;
  isMe: boolean;
}

export interface Period {
  id: string;
  label: string;
  openedAt: string;
  dueOn: string | null;
  paid: number;
  members: number;
  myStatus: ContributionStatus;
  /** membership id → status. Filled for the chairperson and treasurer only. */
  statuses: Record<string, ContributionStatus>;
}

export interface Group {
  stokvel: StokvelProfile;
  me: Member & { sharePhone: boolean };
  members: Member[];
  periods: Period[];
}

export interface StokvelEvent {
  id: string;
  action: string;
  actionLabel: string;
  actorName: string | null;
  subjectName: string | null;
  detail: Record<string, unknown>;
  createdAt: string;
}

export interface StokvelDetails {
  name: string;
  kind: StokvelKind;
  meetingFrequency: Frequency;
  contributionFrequency: Frequency;
  expectedMembers: number;
  contributionAmount: string | null;
  addressLine1: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
}

// ─── Wire shapes ──────────────────────────────────────────────────────────────
type Wire = Record<string, unknown>;

const toMember = (m: Wire): Member => ({
  id: m.id as string,
  userId: m.user_id as string,
  name: m.name as string,
  phone: (m.phone as string | null) ?? null,
  role: m.role as MemberRole,
  status: m.status as MembershipStatus,
  joinedAt: (m.joined_at as string | null) ?? null,
  requestedAt: (m.requested_at as string | null) ?? null,
  isMe: Boolean(m.is_me),
});

const toGroup = (body: Wire): Group => {
  const s = body.stokvel as Wire;
  const me = body.me as Wire;
  return {
    stokvel: {
      id: s.id as string,
      name: s.name as string,
      kind: s.kind as StokvelKind,
      status: s.status as StokvelStatus,
      meetingFrequency: s.meeting_frequency as Frequency,
      contributionFrequency: s.contribution_frequency as Frequency,
      expectedMembers: s.expected_members as number,
      contributionAmount: (s.contribution_amount as string | null) ?? null,
      addressLine1: (s.address_line1 as string) ?? "",
      addressLine2: (s.address_line2 as string) ?? "",
      suburb: (s.suburb as string) ?? "",
      city: (s.city as string) ?? "",
      province: (s.province as string) ?? "",
      postalCode: (s.postal_code as string) ?? "",
      area: (s.area as string) ?? "",
      hasConstitution: Boolean(s.has_constitution),
      constitutionUploadedAt:
        (s.constitution_uploaded_at as string | null) ?? null,
      activatedAt: (s.activated_at as string | null) ?? null,
      inviteCode: (s.invite_code as string | null) ?? null,
      slug: (s.slug as string | null) ?? null,
      isPublic: Boolean(s.is_public),
      acceptingMembers: s.accepting_members !== false,
      about: (s.about as string) ?? "",
      publicReviewReasons: (s.public_review_reasons as string[]) ?? [],
      plan: {
        ...toPlan(s.plan as Wire),
        activeMembers: (s.plan as Wire).active_members as number,
      },
    },
    me: { ...toMember(me), sharePhone: Boolean(me.share_phone) },
    members: (body.members as Wire[]).map(toMember),
    periods: (body.periods as Wire[]).map((p) => ({
      id: p.id as string,
      label: p.label as string,
      openedAt: p.opened_at as string,
      dueOn: (p.due_on as string | null) ?? null,
      paid: p.paid as number,
      members: p.members as number,
      myStatus: p.my_status as ContributionStatus,
      statuses: (p.statuses as Record<string, ContributionStatus>) ?? {},
    })),
  };
};

const toMine = (m: Wire): MyStokvel => ({
  id: m.id as string,
  name: m.name as string,
  kind: m.kind as StokvelKind,
  stokvelStatus: m.stokvel_status as StokvelStatus,
  area: (m.area as string) ?? "",
  memberCount: m.member_count as number,
  role: m.role as MemberRole,
  membershipStatus: m.membership_status as MembershipStatus,
  latestPeriod: m.latest_period
    ? {
        label: (m.latest_period as Wire).label as string,
        paid: (m.latest_period as Wire).paid as number,
        members: (m.latest_period as Wire).members as number,
        myStatus: (m.latest_period as Wire).my_status as ContributionStatus,
      }
    : null,
});

const toWireDetails = (d: Partial<StokvelDetails>): Wire => {
  const wire: Wire = {};
  const map: Record<keyof StokvelDetails, string> = {
    name: "name",
    kind: "kind",
    meetingFrequency: "meeting_frequency",
    contributionFrequency: "contribution_frequency",
    expectedMembers: "expected_members",
    contributionAmount: "contribution_amount",
    addressLine1: "address_line1",
    suburb: "suburb",
    city: "city",
    province: "province",
    postalCode: "postal_code",
  };
  for (const [key, wireKey] of Object.entries(map)) {
    const value = d[key as keyof StokvelDetails];
    if (value !== undefined)
      wire[wireKey] =
        value === "" && key === "contributionAmount" ? null : value;
  }
  return wire;
};

const base = (id: string) => `/stokvels/${id}`;

/** One member's contributions: counts first, then period by period. */
export interface MemberRecord {
  membershipId: string;
  name: string;
  role: MemberRole;
  joinedAt: string | null;
  onTime: number;
  late: number;
  missed: number;
  open: number;
  rows: {
    periodId: string;
    label: string;
    dueOn: string | null;
    status: ContributionStatus;
    missed: boolean;
  }[];
}

const toRecord = (r: Wire): MemberRecord => ({
  membershipId: r.membership_id as string,
  name: r.name as string,
  role: r.role as MemberRole,
  joinedAt: (r.joined_at as string | null) ?? null,
  onTime: r.on_time as number,
  late: r.late as number,
  missed: r.missed as number,
  open: r.open as number,
  rows: (r.rows as Wire[]).map((row) => ({
    periodId: row.period_id as string,
    label: row.label as string,
    dueOn: (row.due_on as string | null) ?? null,
    status: row.status as ContributionStatus,
    missed: Boolean(row.missed),
  })),
});

export const stokvelsApi = {
  async mine(): Promise<MyStokvel[]> {
    return (await api.get<Wire[]>("/stokvels/")).map(toMine);
  },

  async create(details: StokvelDetails): Promise<Group> {
    return toGroup(await api.post<Wire>("/stokvels/", toWireDetails(details)));
  },

  async search(query: string): Promise<SearchResult[]> {
    const rows = await api.get<Wire[]>(
      `/stokvels/search/?q=${encodeURIComponent(query)}`,
    );
    return rows.map((r) => ({
      id: r.id as string,
      name: r.name as string,
      kind: r.kind as StokvelKind,
      status: r.status as StokvelStatus,
      area: (r.area as string) ?? "",
      memberCount: r.member_count as number,
    }));
  },

  async join(code: string): Promise<Group> {
    return toGroup(await api.post<Wire>("/stokvels/join/", { code }));
  },

  async requestToJoin(id: string): Promise<MyStokvel[]> {
    return (await api.post<Wire[]>(`${base(id)}/request/`)).map(toMine);
  },

  async group(id: string): Promise<Group> {
    return toGroup(await api.get<Wire>(`${base(id)}/`));
  },

  async update(id: string, details: Partial<StokvelDetails>): Promise<Group> {
    return toGroup(
      await api.patch<Wire>(`${base(id)}/`, toWireDetails(details)),
    );
  },

  async uploadConstitution(id: string, file: File): Promise<Group> {
    const form = new FormData();
    form.append("file", file);
    return toGroup(await api.upload<Wire>(`${base(id)}/constitution/`, form));
  },

  async constitution(id: string): Promise<Blob> {
    return api.download(`${base(id)}/constitution/`);
  },

  async resetInviteCode(id: string): Promise<Group> {
    return toGroup(await api.post<Wire>(`${base(id)}/invite-code/`));
  },

  async sharePhone(id: string, share: boolean): Promise<Group> {
    return toGroup(await api.post<Wire>(`${base(id)}/share-phone/`, { share }));
  },

  async leave(id: string): Promise<void> {
    await api.post(`${base(id)}/leave/`);
  },

  async memberAction(
    id: string,
    membershipId: string,
    action: MemberAction,
  ): Promise<Group> {
    return toGroup(
      await api.post<Wire>(`${base(id)}/members/${membershipId}/${action}/`),
    );
  },

  async openPeriod(
    id: string,
    label: string,
    dueOn: string | null = null,
  ): Promise<Group> {
    return toGroup(
      await api.post<Wire>(`${base(id)}/periods/`, {
        label,
        due_on: dueOn || null,
      }),
    );
  },

  async memberRecord(id: string, membershipId: string): Promise<MemberRecord> {
    return toRecord(
      await api.get<Wire>(`${base(id)}/members/${membershipId}/history/`),
    );
  },

  async defaulters(id: string): Promise<MemberRecord[]> {
    return (await api.get<Wire[]>(`${base(id)}/defaulters/`)).map(toRecord);
  },

  async recordContribution(
    id: string,
    periodId: string,
    membershipId: string,
    status: ContributionStatus,
  ): Promise<Group> {
    return toGroup(
      await api.put<Wire>(
        `${base(id)}/periods/${periodId}/members/${membershipId}/`,
        { status },
      ),
    );
  },

  async events(id: string): Promise<StokvelEvent[]> {
    const rows = await api.get<Wire[]>(`${base(id)}/events/`);
    return rows.map((e) => ({
      id: e.id as string,
      action: e.action as string,
      actionLabel: e.action_label as string,
      actorName: (e.actor_name as string | null) ?? null,
      subjectName: (e.subject_name as string | null) ?? null,
      detail: (e.detail as Record<string, unknown>) ?? {},
      createdAt: e.created_at as string,
    }));
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Bulk buying
// ═══════════════════════════════════════════════════════════════════════════
export type BulkOrderStatus =
  | "OPEN"
  | "SUBMITTED"
  | "CONFIRMED"
  | "DECLINED"
  | "READY"
  | "COLLECTED"
  | "CANCELLED";

export interface CollectionPoint {
  id: string;
  name: string;
  address: string;
  openingHours: string;
}

export interface WholesalerChoice {
  id: string;
  name: string;
  collectionPoints: CollectionPoint[];
}

export interface Product {
  id: string;
  name: string;
  brand: string;
  packSize: string;
  image: string | null;
  price: string;
  minimumOrderQuantity: number;
  orderIncrement: number;
  tiers: { minimumQuantity: number; unitPrice: string }[];
}

export interface BulkOrderSummary {
  id: string;
  reference: string;
  title: string;
  status: BulkOrderStatus;
  statusLabel: string;
  wholesalerName: string;
  closesOn: string;
  createdAt: string;
}

export interface BulkProductRow {
  product: Product;
  totalQuantity: number;
  unitPrice: string;
  baseUnitPrice: string;
  lineTotal: string;
  myQuantity: number;
  nextTier: { moreNeeded: number; unitPrice: string } | null;
  problem: string;
  /** membership id → packs. Officers only. */
  byMember: Record<string, number>;
}

export interface BulkOrder {
  order: BulkOrderSummary & {
    note: string;
    supplierNote: string;
    collectionPoint: CollectionPoint | null;
    submittedAt: string | null;
    contactName: string;
  };
  products: BulkProductRow[];
  total: string;
  saving: string;
  myTotal: string;
  memberCount: number;
  /** membership id → estimated share. Officers only. */
  shares: Record<string, string>;
}

const toPoint = (p: Wire): CollectionPoint => ({
  id: p.id as string,
  name: p.name as string,
  address: (p.address as string) ?? "",
  openingHours: (p.opening_hours as string) ?? "",
});

const toProduct = (p: Wire): Product => ({
  id: p.id as string,
  name: p.name as string,
  brand: (p.brand as string) ?? "",
  packSize: (p.pack_size as string) ?? "",
  image: (p.image as string | null) ?? null,
  price: p.price as string,
  minimumOrderQuantity: p.minimum_order_quantity as number,
  orderIncrement: p.order_increment as number,
  tiers: ((p.tiers as Wire[]) ?? []).map((t) => ({
    minimumQuantity: t.minimum_quantity as number,
    unitPrice: t.unit_price as string,
  })),
});

const toSummary = (o: Wire): BulkOrderSummary => ({
  id: o.id as string,
  reference: o.reference as string,
  title: o.title as string,
  status: o.status as BulkOrderStatus,
  statusLabel: o.status_label as string,
  wholesalerName: o.wholesaler_name as string,
  closesOn: o.closes_on as string,
  createdAt: o.created_at as string,
});

const toBulkOrder = (body: Wire): BulkOrder => {
  const o = body.order as Wire;
  return {
    order: {
      ...toSummary(o),
      note: (o.note as string) ?? "",
      supplierNote: (o.supplier_note as string) ?? "",
      collectionPoint: o.collection_point
        ? toPoint(o.collection_point as Wire)
        : null,
      submittedAt: (o.submitted_at as string | null) ?? null,
      contactName: (o.contact_name as string) ?? "",
    },
    products: (body.products as Wire[]).map((r) => {
      const next = r.next_tier as Wire | null;
      return {
        product: toProduct(r.product as Wire),
        totalQuantity: r.total_quantity as number,
        unitPrice: r.unit_price as string,
        baseUnitPrice: r.base_unit_price as string,
        lineTotal: r.line_total as string,
        myQuantity: r.my_quantity as number,
        nextTier: next
          ? {
              moreNeeded: next.more_needed as number,
              unitPrice: next.unit_price as string,
            }
          : null,
        problem: (r.problem as string) ?? "",
        byMember: (r.by_member as Record<string, number>) ?? {},
      };
    }),
    total: body.total as string,
    saving: body.saving as string,
    myTotal: body.my_total as string,
    memberCount: body.member_count as number,
    shares: (body.shares as Record<string, string>) ?? {},
  };
};

const bulk = (id: string, orderId?: string) =>
  orderId ? `/stokvels/${id}/bulk/${orderId}` : `/stokvels/${id}/bulk`;

export const bulkApi = {
  async wholesalers(): Promise<WholesalerChoice[]> {
    const rows = await api.get<Wire[]>("/stokvels/wholesalers/");
    return rows.map((w) => ({
      id: w.id as string,
      name: w.name as string,
      collectionPoints: ((w.collection_points as Wire[]) ?? []).map(toPoint),
    }));
  },

  async list(id: string): Promise<BulkOrderSummary[]> {
    return (await api.get<Wire[]>(`${bulk(id)}/`)).map(toSummary);
  },

  async start(
    id: string,
    details: {
      wholesalerId: string;
      title: string;
      closesOn: string;
      collectionPointId: string | null;
      note: string;
    },
  ): Promise<BulkOrder> {
    return toBulkOrder(
      await api.post<Wire>(`${bulk(id)}/`, {
        wholesaler_id: details.wholesalerId,
        title: details.title,
        closes_on: details.closesOn,
        collection_point_id: details.collectionPointId,
        note: details.note,
      }),
    );
  },

  async get(id: string, orderId: string): Promise<BulkOrder> {
    return toBulkOrder(await api.get<Wire>(`${bulk(id, orderId)}/`));
  },

  async catalogue(
    id: string,
    orderId: string,
    query: string,
  ): Promise<Product[]> {
    const rows = await api.get<Wire[]>(
      `${bulk(id, orderId)}/catalogue/?q=${encodeURIComponent(query)}`,
    );
    return rows.map(toProduct);
  },

  async setQuantity(
    id: string,
    orderId: string,
    listingId: string,
    quantity: number,
  ): Promise<BulkOrder> {
    return toBulkOrder(
      await api.put<Wire>(`${bulk(id, orderId)}/items/${listingId}/`, {
        quantity,
      }),
    );
  },

  async submit(
    id: string,
    orderId: string,
    contactName: string,
    contactPhone: string,
  ): Promise<BulkOrder> {
    return toBulkOrder(
      await api.post<Wire>(`${bulk(id, orderId)}/submit/`, {
        contact_name: contactName,
        contact_phone: contactPhone,
      }),
    );
  },

  async cancel(
    id: string,
    orderId: string,
    reason: string,
  ): Promise<BulkOrder> {
    return toBulkOrder(
      await api.post<Wire>(`${bulk(id, orderId)}/cancel/`, { reason }),
    );
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// The shared marketplace
// ═══════════════════════════════════════════════════════════════════════════
export type ListingKind = "PRODUCT" | "SERVICE" | "BUSINESS";
export type ContactPreference = "IN_APP" | "WHATSAPP" | "CALL";
export type ListingStatus =
  "PUBLISHED" | "IN_REVIEW" | "HIDDEN" | "PAUSED" | "REJECTED" | "WITHDRAWN";
export type ReportReason =
  "PROHIBITED" | "SCAM" | "MISLEADING" | "OFFENSIVE" | "OTHER";

export const REPORT_REASONS: Record<ReportReason, string> = {
  PROHIBITED: "Not allowed on SmartSpaza",
  SCAM: "Looks like a scam or money scheme",
  MISLEADING: "Misleading",
  OFFENSIVE: "Offensive",
  OTHER: "Something else",
};

export interface MarketItem {
  source: "member" | "wholesaler";
  id: string;
  title: string;
  seller: string;
  tag: string;
  category: string;
  price: string | null;
  image: string | null;
  area: string;
}

export interface Market {
  maySell: boolean;
  categories: { code: string; label: string }[];
  items: MarketItem[];
}

export interface ListingDetail {
  id: string;
  kind: ListingKind;
  title: string;
  category: string;
  categoryLabel: string;
  description: string;
  price: string | null;
  contactPreference: ContactPreference;
  seller: string;
  area: string;
  photos: string[];
  phone: string | null;
  /** The seller's own view only. */
  status: ListingStatus | null;
  reviewReasons: string[];
  isMine: boolean;
  sellerId: string;
  /** `average` is null until the seller has enough ratings to show one (§5.1). */
  sellerRating: { count: number; average: string | null };
}

export interface MyListing {
  id: string;
  title: string;
  status: ListingStatus;
  statusLabel: string;
  reviewReasons: string[];
  price: string | null;
  image: string | null;
}

export interface NewListing {
  kind: ListingKind;
  title: string;
  category: string;
  description: string;
  price: string;
  contactPreference: ContactPreference;
  suburb: string;
  city: string;
  province: string;
  stokvelId: string;
  photos: File[];
}

const toListing = (l: Wire): ListingDetail => ({
  id: l.id as string,
  kind: l.kind as ListingKind,
  title: l.title as string,
  category: l.category as string,
  categoryLabel: l.category_label as string,
  description: (l.description as string) ?? "",
  price: (l.price as string | null) ?? null,
  contactPreference: l.contact_preference as ContactPreference,
  seller: l.seller as string,
  area: (l.area as string) ?? "",
  photos: (l.photos as string[]) ?? [],
  phone: (l.phone as string | null) ?? null,
  status: (l.status as ListingStatus | null) ?? null,
  reviewReasons: (l.review_reasons as string[]) ?? [],
  isMine: Boolean(l.is_mine),
  sellerId: l.seller_id as string,
  sellerRating: (l.seller_rating as {
    count: number;
    average: string | null;
  }) ?? {
    count: 0,
    average: null,
  },
});

export const marketApi = {
  async browse(params: {
    q?: string;
    category?: string;
    source?: string;
  }): Promise<Market> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params))
      if (value) query.set(key, value);
    const body = await api.get<Wire>(`/stokvels/market/?${query.toString()}`);
    return {
      maySell: Boolean(body.may_sell),
      categories: body.categories as { code: string; label: string }[],
      items: (body.results as Wire[]).map((i) => ({
        source: i.source as "member" | "wholesaler",
        id: i.id as string,
        title: i.title as string,
        seller: i.seller as string,
        tag: i.tag as string,
        category: i.category as string,
        price: (i.price as string | null) ?? null,
        image: (i.image as string | null) ?? null,
        area: (i.area as string) ?? "",
      })),
    };
  },

  async listing(id: string): Promise<ListingDetail> {
    return toListing(await api.get<Wire>(`/stokvels/market/listings/${id}/`));
  },

  async sell(listing: NewListing): Promise<ListingDetail> {
    const form = new FormData();
    form.append("kind", listing.kind);
    form.append("title", listing.title);
    form.append("category", listing.category);
    form.append("description", listing.description);
    if (listing.price) form.append("price", listing.price);
    form.append("contact_preference", listing.contactPreference);
    form.append("suburb", listing.suburb);
    form.append("city", listing.city);
    form.append("province", listing.province);
    if (listing.stokvelId) form.append("stokvel_id", listing.stokvelId);
    for (const photo of listing.photos) form.append("photos", photo);
    return toListing(
      await api.upload<Wire>("/stokvels/market/listings/", form),
    );
  },

  async mine(): Promise<MyListing[]> {
    const rows = await api.get<Wire[]>("/stokvels/market/listings/mine/");
    return rows.map((l) => ({
      id: l.id as string,
      title: l.title as string,
      status: l.status as ListingStatus,
      statusLabel: l.status_label as string,
      reviewReasons: (l.review_reasons as string[]) ?? [],
      price: (l.price as string | null) ?? null,
      image: (l.image as string | null) ?? null,
    }));
  },

  async withdraw(id: string): Promise<void> {
    await api.post(`/stokvels/market/listings/${id}/withdraw/`);
  },

  async report(id: string, reason: ReportReason, note: string): Promise<void> {
    await api.post(`/stokvels/market/listings/${id}/report/`, { reason, note });
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Conversations between buyers and sellers
// ═══════════════════════════════════════════════════════════════════════════
export interface ConversationSummary {
  id: string;
  listingId: string;
  listingTitle: string;
  withName: string;
  iAm: "buyer" | "seller";
  unread: boolean;
  dealDone: boolean;
  lastMessageAt: string | null;
}

export interface Conversation extends ConversationSummary {
  /** The other person's number — only once they chose to share it here. */
  otherPhone: string | null;
  iSharedPhone: boolean;
  sellerId: string;
  rated: boolean;
  messages: { id: string; mine: boolean; body: string; at: string }[];
}

const toSummaryConversation = (c: Wire): ConversationSummary => ({
  id: c.id as string,
  listingId: c.listing_id as string,
  listingTitle: c.listing_title as string,
  withName: c.with_name as string,
  iAm: c.i_am as "buyer" | "seller",
  unread: Boolean(c.unread),
  dealDone: Boolean(c.deal_done),
  lastMessageAt: (c.last_message_at as string | null) ?? null,
});

const toConversation = (c: Wire): Conversation => ({
  ...toSummaryConversation(c),
  otherPhone: (c.other_phone as string | null) ?? null,
  iSharedPhone: Boolean(c.i_shared_phone),
  sellerId: c.seller_id as string,
  rated: Boolean(c.rated),
  messages: (c.messages as Wire[]).map((m) => ({
    id: m.id as string,
    mine: Boolean(m.mine),
    body: m.body as string,
    at: m.at as string,
  })),
});

export const conversationsApi = {
  async start(listingId: string, body: string): Promise<Conversation> {
    return toConversation(
      await api.post<Wire>(`/stokvels/market/listings/${listingId}/enquire/`, {
        body,
      }),
    );
  },
  async list(): Promise<ConversationSummary[]> {
    return (await api.get<Wire[]>("/stokvels/messages/")).map(
      toSummaryConversation,
    );
  },
  async get(id: string): Promise<Conversation> {
    return toConversation(await api.get<Wire>(`/stokvels/messages/${id}/`));
  },
  async reply(id: string, body: string): Promise<Conversation> {
    return toConversation(
      await api.post<Wire>(`/stokvels/messages/${id}/reply/`, { body }),
    );
  },
  async sharePhone(id: string): Promise<Conversation> {
    return toConversation(
      await api.post<Wire>(`/stokvels/messages/${id}/share-phone/`),
    );
  },
  async dealDone(id: string): Promise<Conversation> {
    return toConversation(
      await api.post<Wire>(`/stokvels/messages/${id}/deal-done/`),
    );
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Ratings
// ═══════════════════════════════════════════════════════════════════════════
export interface Rating {
  id: string;
  overall: string;
  quality: number;
  communication: number;
  onTime: number;
  comment: string;
  rater: string;
  reply: string;
  reported: boolean;
  createdAt: string;
}

export interface SellerProfile {
  id: string;
  name: string;
  isMe: boolean;
  count: number;
  average: string | null;
  ratings: Rating[];
}

const toRating = (r: Wire): Rating => ({
  id: r.id as string,
  overall: r.overall as string,
  quality: r.quality as number,
  communication: r.communication as number,
  onTime: r.on_time as number,
  comment: (r.comment as string) ?? "",
  rater: r.rater as string,
  reply: (r.reply as string) ?? "",
  reported: Boolean(r.reported),
  createdAt: r.created_at as string,
});

export const ratingsApi = {
  async rate(
    enquiryId: string,
    stars: {
      quality: number;
      communication: number;
      onTime: number;
      comment: string;
    },
  ): Promise<Rating> {
    return toRating(
      await api.post<Wire>(`/stokvels/messages/${enquiryId}/rate/`, {
        quality: stars.quality,
        communication: stars.communication,
        on_time: stars.onTime,
        comment: stars.comment,
      }),
    );
  },
  async seller(userId: string): Promise<SellerProfile> {
    const body = await api.get<Wire>(`/stokvels/sellers/${userId}/`);
    return {
      id: body.id as string,
      name: body.name as string,
      isMe: Boolean(body.is_me),
      count: body.count as number,
      average: (body.average as string | null) ?? null,
      ratings: (body.ratings as Wire[]).map(toRating),
    };
  },
  async reply(ratingId: string, text: string): Promise<Rating> {
    return toRating(
      await api.post<Wire>(`/stokvels/ratings/${ratingId}/reply/`, { text }),
    );
  },
  async report(ratingId: string, text: string): Promise<Rating> {
    return toRating(
      await api.post<Wire>(`/stokvels/ratings/${ratingId}/report/`, { text }),
    );
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// The Stokkie Stokvel website: stokvels' public pages (no sign-in)
// ═══════════════════════════════════════════════════════════════════════════
export interface PublicStokvel {
  id: string;
  slug: string;
  name: string;
  kind: StokvelKind;
  kindLabel: string;
  about: string;
  area: string;
  province: string;
  memberCount: number;
  meetingFrequency: string;
  contributionFrequency: string;
  acceptingMembers: boolean;
  since: number;
}

const toPublic = (s: Wire): PublicStokvel => ({
  id: s.id as string,
  slug: s.slug as string,
  name: s.name as string,
  kind: s.kind as StokvelKind,
  kindLabel: s.kind_label as string,
  about: (s.about as string) ?? "",
  area: (s.area as string) ?? "",
  province: (s.province as string) ?? "",
  memberCount: s.member_count as number,
  meetingFrequency: s.meeting_frequency as string,
  contributionFrequency: s.contribution_frequency as string,
  acceptingMembers: Boolean(s.accepting_members),
  since: s.since as number,
});

export const publicApi = {
  async plans(): Promise<Plan[]> {
    return (await api.get<Wire[]>("/public/stokvels/plans/")).map(toPlan);
  },
  async stokvels(): Promise<PublicStokvel[]> {
    return (await api.get<Wire[]>("/public/stokvels/")).map(toPublic);
  },
  async stokvel(slug: string): Promise<PublicStokvel> {
    return toPublic(await api.get<Wire>(`/public/stokvels/${slug}/`));
  },
};

export const publicPageApi = {
  async save(
    stokvelId: string,
    page: { isPublic: boolean; acceptingMembers: boolean; about: string },
  ): Promise<Group> {
    return toGroup(
      await api.post<Wire>(`/stokvels/${stokvelId}/public-page/`, {
        is_public: page.isPublic,
        accepting_members: page.acceptingMembers,
        about: page.about,
      }),
    );
  },
};
